import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useData } from '../../contexts/DataContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { toast } from 'sonner';
import DriveAttachGate from '../files/DriveAttachGate';
import { uploadTaskFilesToDrive, formatUploadErrors } from '../../utils/driveUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddTaskModal = ({ onClose, onSuccess, parentTask, initialData }) => {
  const { t } = useLanguage();
  const { contacts, categories, createTask, fetchContacts, fetchCategories } = useData();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });
  
  // Media capture states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // Fetch contacts and categories when modal opens
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchContacts(),
          fetchCategories()
        ]);
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };
    
    loadData();
  }, [fetchContacts, fetchCategories]);

  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    due_date: initialData?.due_date || "",
    time: initialData?.time || "",
    category: initialData?.category || "",
    priority: initialData?.priority || "Medium",
    location: initialData?.location || "",
    contact_id: initialData?.contact_id || "",
    notes: initialData?.notes || "",
    recurring: initialData?.recurring || false,
    recurrence_pattern: initialData?.recurrence_pattern || "",
    recurrence_end_date: initialData?.recurrence_end_date || "",
    parent_task_id: initialData?.parent_task_id || null
  });

  // Check if this is a follow-up task
  const isFollowUpTask = Boolean(parentTask || formData.parent_task_id);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        const file = new File([blob], `recording-${Date.now()}.wav`, { type: 'audio/wav' });
        setAttachments(prev => [...prev, file]);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Format dates properly
      const taskData = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        recurrence_end_date: formData.recurrence_end_date ? new Date(formData.recurrence_end_date).toISOString() : null
      };

      // Create the task
      const response = await createTask(taskData);
      const taskId = response.id;

      // Handle file attachments if any
      if (attachments.length > 0) {
        setUploadProgress({ current: 0, total: attachments.length, filename: '' });
        
        const uploadErrors = await uploadTaskFilesToDrive(taskId, attachments, {
          onFileStart: (i, file) => setUploadProgress({ current: i + 1, total: attachments.length, filename: file.name })
        });
        if (uploadErrors.length) toast.error(`Some files were not attached:\n${formatUploadErrors(uploadErrors)}`);
      }

      onSuccess && onSuccess(response);
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    } else if (file.type.startsWith('audio/')) {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isFollowUpTask ? t('createFollowUpTask') : t('addNewTask')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Follow-up Task Info */}
        {isFollowUpTask && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h3 className="text-sm font-medium text-blue-900">{t('followUpTask')}</h3>
            </div>
            <p className="text-sm text-blue-700">{t('followUpTaskLinked')}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('title')} *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="mobile-input"
              required
              placeholder={t('enterTaskTitle')}
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('dueDate')} *</label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleInputChange}
              className="mobile-input"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('time')} *</label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleInputChange}
              className="mobile-input"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('category')} *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="mobile-input"
              required
            >
              <option value="">{t('selectCategory')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('priority')} *</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              className="mobile-input"
              required
            >
              <option value="Low">{t('low')}</option>
              <option value="Medium">{t('medium')}</option>
              <option value="High">{t('high')}</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('location')}</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              className="mobile-input"
              placeholder={t('enterLocation')}
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">{t('contact')}</label>
            <select
              name="contact_id"
              value={formData.contact_id}
              onChange={handleInputChange}
              className="mobile-input"
            >
              <option value="">{t('selectContact')}</option>
              {contacts.map(contact => (
                <option key={contact.id} value={contact.id}>{contact.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-2">{t('notes')}</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="mobile-input resize-none"
            placeholder={t('addNotes')}
          />
        </div>

        {/* Recurring Task Options */}
        <div className="border-t pt-4">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              name="recurring"
              checked={formData.recurring}
              onChange={handleInputChange}
              className="mr-3 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="text-gray-700 font-medium">{t('recurring')} Task</label>
          </div>

          {formData.recurring && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('recurrencePattern')}</label>
                <select
                  name="recurrence_pattern"
                  value={formData.recurrence_pattern}
                  onChange={handleInputChange}
                  className="mobile-input"
                  required={formData.recurring}
                >
                  <option value="">{t('selectPattern')}</option>
                  <option value="daily">{t('daily')}</option>
                  <option value="weekly">{t('weekly')}</option>
                  <option value="monthly">{t('monthly')}</option>
                  <option value="yearly">{t('yearly')}</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">{t('endDate')}</label>
                <input
                  type="date"
                  name="recurrence_end_date"
                  value={formData.recurrence_end_date}
                  onChange={handleInputChange}
                  className="mobile-input"
                  required={formData.recurring}
                />
              </div>
            </div>
          )}
        </div>

        {/* File Attachments */}
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-900 mb-4">{t('attachmentsMedia')}</h3>
          
          <DriveAttachGate>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                accept="*/*"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-600 mt-2">{t('uploadFiles')}</p>
              </label>
            </div>

            {/* Audio Recording */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                className={`mx-auto h-8 w-8 rounded-full flex items-center justify-center ${
                  isRecording ? 'bg-red-500 text-white animate-pulse' : 'text-gray-400'
                }`}
              >
                {isRecording ? '⏹' : '🎤'}
              </button>
              <p className="text-sm text-gray-600 mt-2">
                {isRecording ? t('recording') : t('recordAudio')}
              </p>
            </div>

            {/* Photo Capture - Placeholder */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-sm text-gray-600 mt-2">{t('takePhoto')}</p>
            </div>
          </div>
          </DriveAttachGate>

          {/* Selected Files Preview */}
          {attachments.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Files ({attachments.length})</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="flex-shrink-0 mr-2 text-blue-600">
                        {getFileIcon(file)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="ml-2 flex-shrink-0 text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploadProgress.total > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span>{t('uploading')}: {uploadProgress.filename}</span>
                <span>{uploadProgress.current}/{uploadProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex space-x-3 pt-4 border-t">
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={onClose}
            disabled={loading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={loading}
          >
            {loading ? (isFollowUpTask ? t('creatingFollowUp') : t('creating')) : (isFollowUpTask ? t('createFollowUpTask') : t('createTask'))}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddTaskModal;